import { toast } from '@navi/web-ui/lib/primitives/Toast';
import { Dispatch } from '@reduxjs/toolkit';
import axiosInstance, { ApiKeys, getApiUrl } from '../../../utils/ApiHelper';
import { STORE_KEYS } from '../constants';
import {
  COMMUNICATION_HISTORY_TAB_KEYS,
  SYSTEM_INTERACTIONS_TYPE
} from '../constants/communicationHistory.constant';
import { IWAInteractionPayload } from '../interfaces/Interactions.type';
import {
  resetCaseDetail,
  setCaseDetail,
  setCaseDetailError,
  setCaseDetailLoading
} from '../reducers/caseDetailSlice';
import { downloadDocumentFromS3WithoughtPreview } from '@cp/src/utils/DocumentDownloadFromS3';
import { DUNNING_LETTER_NAME } from '../components/CommunicationHistory/constants';
import { DateFormat, dateFormat } from '@cp/src/utils/DateHelper';
import { IEscalationPayload } from '../interfaces/Escalations.types';
import { IFieldCallInteractionsPayload } from '../components/CommunicationHistory/type';

export const getWAInteractions =
  (
    lan: string,
    customerRefrenceId: string,
    waInteractionsPayload: IWAInteractionPayload = {
      customerReferenceId: customerRefrenceId,
      size: 10,
      page: 0
    },
    caseReferenceId: string
  ) =>
  (dispatch: Dispatch) => {
    dispatch(
      setCaseDetailLoading({
        lan,
        customerRefrenceId,
        key: STORE_KEYS.WA_INTERACTIONS,
        loading: true
      })
    );
    axiosInstance
      .get(getApiUrl(ApiKeys.WA_INTERACTIONS), {
        headers: { caseReferenceId },
        params: waInteractionsPayload
      })
      .then(res => {
        dispatch(
          setCaseDetail({
            lan,
            customerRefrenceId,
            key: STORE_KEYS.WA_INTERACTIONS,
            data: res.data
          })
        );
      })
      .catch(() => {
        dispatch(
          setCaseDetailError({
            lan,
            customerRefrenceId,
            key: STORE_KEYS.WA_INTERACTIONS
          })
        );
      });
  };

export const getEscalations =
  (
    lan: string,
    customerRefrenceId: string,
    escalationPayload: IEscalationPayload = {
      page_no: 10,
      page_size: 0
    }
  ) =>
  (dispatch: Dispatch) => {
    dispatch(
      setCaseDetailLoading({
        lan,
        customerRefrenceId,
        key: STORE_KEYS.ESCALATIONS,
        loading: true
      })
    );
    axiosInstance
      .get(getApiUrl(ApiKeys.ESCALATIONS, null, { loan_account_number: lan, ...escalationPayload }))
      .then(res => {
        dispatch(
          setCaseDetail({
            lan,
            customerRefrenceId,
            key: STORE_KEYS.ESCALATIONS,
            data: res.data
          })
        );
      })
      .catch(() => {
        dispatch(
          setCaseDetailError({
            lan,
            customerRefrenceId,
            key: STORE_KEYS.ESCALATIONS
          })
        );
      });
  };

export const fetchSystemInteractions =
  (lan: string, customerRefrenceId: string, payload: Record<string, any>, oldLAN: string) =>
  (dispatch: Dispatch) => {
    dispatch(
      setCaseDetailLoading({
        lan,
        customerRefrenceId,
        key: COMMUNICATION_HISTORY_TAB_KEYS[payload.communication_type],
        loading: true
      })
    );
    axiosInstance
      .get(getApiUrl(ApiKeys.DIGITAL_COMMUNICATION), {
        params: payload,
        headers: { loanaccountnumber: oldLAN || lan }
      })
      .then(res => {
        dispatch(
          setCaseDetail({
            lan,
            customerRefrenceId,
            key: COMMUNICATION_HISTORY_TAB_KEYS[payload.communication_type],
            data: res.data,
            loading: false
          })
        );
      })
      .catch(() => {
        dispatch(
          setCaseDetailError({
            lan,
            customerRefrenceId,
            key: COMMUNICATION_HISTORY_TAB_KEYS[payload.communication_type],
            loading: false
          })
        );
      });
  };

export const fetchFieldSystemInteractions =
  (lan: string, customerRefrenceId: string, payload: any, isPreviousLoanInteraction: boolean) =>
  (dispatch: Dispatch) => {
    dispatch(
      setCaseDetailLoading({
        lan,
        customerRefrenceId,
        key: COMMUNICATION_HISTORY_TAB_KEYS[payload.types],
        loading: true
      })
    );
    axiosInstance
      .get(
        getApiUrl(
          isPreviousLoanInteraction ? ApiKeys.PREVIOUS_LAN_INTERACTIONS : ApiKeys.FIELD_FEEDBACK
        ),
        {
          params: payload,
          headers: { loanaccountnumber: lan, includeInUptimeMetrics: true }
        }
      )
      .then(res => {
        dispatch(
          setCaseDetail({
            lan,
            customerRefrenceId,
            key: COMMUNICATION_HISTORY_TAB_KEYS[SYSTEM_INTERACTIONS_TYPE.FIELD_VISIT],
            data: res.data,
            loading: false
          })
        );
      })
      .catch(() => {
        dispatch(
          setCaseDetailError({
            lan,
            customerRefrenceId,
            key: COMMUNICATION_HISTORY_TAB_KEYS[payload.types],
            loading: false
          })
        );
      });
  };

export const fetchCallInteractions =
  (
    lan: string,
    customerRefrenceId: string,
    payload: Record<string, any>,
    isPreviousLanInteraction: boolean
  ) =>
  (dispatch: Dispatch) => {
    dispatch(
      setCaseDetailLoading({
        lan,
        customerRefrenceId,
        key: STORE_KEYS.CALL_INTERACTIONS,
        loading: true
      })
    );
    axiosInstance
      .get(
        isPreviousLanInteraction
          ? getApiUrl(ApiKeys.PREVIOUS_LAN_INTERACTIONS)
          : getApiUrl(ApiKeys.CALL_INTERACTIONS),
        {
          params: payload,
          headers: { includeInUptimeMetrics: true }
        }
      )
      .then(res => {
        dispatch(
          setCaseDetail({
            lan,
            customerRefrenceId,
            key: STORE_KEYS.CALL_INTERACTIONS,
            data: res.data,
            loading: false
          })
        );
      })
      .catch(() => {
        dispatch(
          setCaseDetailError({
            lan,
            customerRefrenceId,
            key: STORE_KEYS.CALL_INTERACTIONS,
            loading: false
          })
        );
        dispatch(
          resetCaseDetail({
            lan,
            customerRefrenceId,
            key: STORE_KEYS.CALL_INTERACTIONS,
            loading: false
          })
        );
      });
  };

export const fetchFieldCallInteractions =
  (lan: string, customerRefrenceId: string, payload: IFieldCallInteractionsPayload) =>
  (dispatch: Dispatch) => {
    dispatch(
      setCaseDetailLoading({
        lan,
        customerRefrenceId,
        key: STORE_KEYS.FIELD_CALL_INTERACTIONS,
        loading: true
      })
    );
    const url = getApiUrl(ApiKeys.GET_FIELD_CALL_INTERACTIONS);

    axiosInstance
      .get(url, {
        headers: {
          donotHandleError: true
        },
        params: payload
      })
      .then(res => {
        if (res?.data) {
          dispatch(
            setCaseDetail({
              lan,
              customerRefrenceId,
              key: STORE_KEYS.FIELD_CALL_INTERACTIONS,
              data: res.data,
              loading: false
            })
          );
        }
      })
      .catch(() => {
        dispatch(
          setCaseDetailError({
            lan,
            customerRefrenceId,
            key: STORE_KEYS.FIELD_CALL_INTERACTIONS,
            loading: false
          })
        );
        dispatch(
          resetCaseDetail({
            lan,
            customerRefrenceId,
            key: STORE_KEYS.FIELD_CALL_INTERACTIONS,
            loading: false
          })
        );
      });
  };

export const downloadDunningLetter = (
  lan: string,
  customerRefrenceId: string,
  downloading: React.Dispatch<React.SetStateAction<boolean>>
) => {
  downloading(true);
  axiosInstance
    .post(
      getApiUrl(ApiKeys.DOWNLOAD_DUNNING_LETTER),
      {
        documentGenerationType: 'DUNNING_LETTER',
        loanAccountNumber: lan
      },
      {
        headers: {
          loanaccountnumber: lan,
          customerreferenceid: customerRefrenceId,
          donotHandleError: true
        }
      }
    )
    .then(res => {
      if (res?.data?.uri) {
        const date = new Date();
        const dunningLetterName = `${DUNNING_LETTER_NAME}_${lan}_${dateFormat(
          date,
          DateFormat.DD_MMM_YYYY
        )}.pdf`;
        downloadDocumentFromS3WithoughtPreview(
          res.data.uri,
          `${dunningLetterName}`,
          `Cannot Download Dunning Letter for LAN: ${lan}`
        );
      }
    })
    .catch(() => {
      toast(`Cannot Download Dunning Letter for LAN: ${lan}`, { type: 'error' });
    })
    .finally(() => downloading(false));
};
